import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductList } from '../api/queries';
import './global-search.css';

/**
 * Global search, closing UC-01-002 — "find products, processes, applications
 * and decisions without knowing the hierarchy".
 *
 * A palette rather than a search page. The use case is explicit that the user
 * does not know where the thing lives, which makes a destination list the wrong
 * answer and a keystroke-reachable overlay the right one.
 *
 * It searches two populations that behave differently and are therefore kept
 * visibly separate: *destinations*, which always exist and are cheap, and
 * *records*, which come from the product projection and are subject to the same
 * scope and masking rules as every other screen. Mixing them into one ranked
 * list would let a scope-filtered result silently outrank a screen the user can
 * always reach.
 */

export interface SearchDestination {
  id: string;
  label: string;
  to: string;
  /** The nav group, shown as the destination's context. */
  group: string | null;
}

const RECENTS_KEY = 'aurora.pvi.recent-views.v1';
const RECENTS_MAX = 6;

export interface RecentView {
  to: string;
  label: string;
  at: number;
}

function readRecents(): RecentView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // Hand-validated rather than trusted: this is user-writable storage, and a
    // malformed entry must not be able to crash the shell on boot.
    return parsed.filter(
      (v): v is RecentView =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as RecentView).to === 'string' &&
        typeof (v as RecentView).label === 'string',
    );
  } catch {
    return [];
  }
}

/** Records a visit. Called by the shell on every navigation. */
export function rememberView(to: string, label: string): void {
  if (typeof window === 'undefined') return;
  try {
    const next = [{ to, label, at: Date.now() }, ...readRecents().filter((v) => v.to !== to)].slice(
      0,
      RECENTS_MAX,
    );
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // A browser with storage disabled loses the recents list and nothing else.
  }
}

export interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
  destinations: SearchDestination[];
}

export function GlobalSearch({ open, onClose, destinations }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Products are only fetched while the palette is open; a search overlay
  // should not add a query to every screen's first paint.
  const productQuery = useProductList();
  const products = open ? (productQuery.data?.data?.rows ?? []) : [];

  const recents = useMemo(() => (open ? readRecents() : []), [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    // Focus after paint, or the dialog steals it before it is on screen.
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return {
        destinations: destinations.slice(0, 5),
        products: [],
        recents,
      };
    }
    const matchedDestinations = destinations
      .filter((d) => d.label.toLowerCase().includes(q) || (d.group ?? '').toLowerCase().includes(q))
      .slice(0, 5);
    const matchedProducts = products
      .filter((p) => p.name.toLowerCase().includes(q) || p.businessCode.toLowerCase().includes(q))
      .slice(0, 6);
    return { destinations: matchedDestinations, products: matchedProducts, recents: [] };
  }, [query, destinations, products, recents]);

  // One flat list of what is actually selectable, so arrow keys traverse the
  // groups in the order they are read rather than per-group.
  const flat = useMemo(
    () => [
      ...results.recents.map((r) => ({ key: `r:${r.to}`, to: r.to })),
      ...results.destinations.map((d) => ({ key: `d:${d.id}`, to: d.to })),
      ...results.products.map((p) => ({ key: `p:${p.id}`, to: `/v2/products/${p.id}/overview` })),
    ],
    [results],
  );

  const go = useCallback(
    (to: string) => {
      onClose();
      navigate(to);
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
        return;
      }
      if (event.key === 'Enter') {
        const target = flat[active];
        if (target) {
          event.preventDefault();
          go(target.to);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, active, go, onClose]);

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  let index = -1;
  const row = (key: string, to: string, primary: string, secondary: string | null) => {
    index += 1;
    const i = index;
    return (
      <li key={key}>
        <button
          type="button"
          data-active={i === active}
          className="g-search-row"
          onMouseEnter={() => setActive(i)}
          onClick={() => go(to)}
        >
          <span className="truncate text-[13px] text-[var(--g-text)]">{primary}</span>
          {secondary ? (
            <span className="shrink-0 text-[11px] text-[var(--g-text-muted)]">{secondary}</span>
          ) : null}
        </button>
      </li>
    );
  };

  const nothing =
    query.trim() &&
    !results.destinations.length &&
    !results.products.length &&
    !productQuery.isPending;

  return (
    <div className="g-search-scrim" onClick={onClose} role="presentation">
      <div
        className="g-search-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="g-search-field">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            placeholder="Search products and screens"
            aria-label="Search products and screens"
            className="g-search-input"
          />
          <kbd className="g-search-kbd">Esc</kbd>
        </div>

        <ul ref={listRef} className="g-search-list">
          {results.recents.length ? (
            <>
              <li className="g-search-heading">Recent</li>
              {results.recents.map((r) => row(`r:${r.to}`, r.to, r.label, 'Recent'))}
            </>
          ) : null}

          {results.destinations.length ? (
            <>
              <li className="g-search-heading">Screens</li>
              {results.destinations.map((d) => row(`d:${d.id}`, d.to, d.label, d.group))}
            </>
          ) : null}

          {results.products.length ? (
            <>
              <li className="g-search-heading">Products</li>
              {results.products.map((p) =>
                row(`p:${p.id}`, `/v2/products/${p.id}/overview`, p.name, p.businessCode),
              )}
            </>
          ) : null}

          {/* Pending and empty are different answers and must not look alike:
              one means "still looking", the other means "nothing matches". */}
          {query.trim() && productQuery.isPending ? (
            <li className="g-search-note">Searching products…</li>
          ) : null}
          {nothing ? (
            <li className="g-search-note">
              Nothing matches “{query.trim()}”. Products outside your access scope are not searched.
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}
